import type { PaletteMode } from '@mui/material';
import { alpha, createTheme } from '@mui/material/styles';
import type { Theme, ThemeOptions } from '@mui/material/styles';

import { buildPalette } from './palette';
import { typography } from './typography';
import { BORDER_RADIUS, BREAKPOINTS, BUTTON_SHAPE, SPACING_UNIT, TABLE_SHAPE } from './tokens';

export interface AppThemeOptions {
  mode?: PaletteMode;
  cor?: string | null;
}

// Aplicado via `styleOverrides.root` (não `variants`), porque vale para os três
// `variant` do MUI (`contained`, `outlined`, `text`) por igual.
const components: ThemeOptions['components'] = {
  MuiButton: {
    // O `contained` do MUI tem elevação por padrão (shadow em repouso, hover, active
    // e foco); a forma oficial do botão não tem sombra em nenhum estado, só o raio,
    // a tipografia e as dimensões cobertos por `styleOverrides` abaixo.
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        borderRadius: BUTTON_SHAPE.borderRadius,
        fontFamily: BUTTON_SHAPE.fontFamily,
        fontWeight: BUTTON_SHAPE.fontWeight,
        lineHeight: BUTTON_SHAPE.lineHeight,
        textTransform: 'none',
      },
      // Único tamanho declarado em BUTTON_SHAPE: `small`/`large` seguem a escala
      // default do MUI.
      sizeMedium: {
        height: BUTTON_SHAPE.height,
        paddingLeft: BUTTON_SHAPE.paddingX,
        paddingRight: BUTTON_SHAPE.paddingX,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      // A célula de seleção (`padding="checkbox"`) fica de fora do padding compacto
      // via seletor explícito: sua densidade própria (width 48, padding '0 0 0 4px',
      // aplicada pelo próprio MUI) já é mais compacta que o token da tabela, e a
      // ordem de merge dos slots de override não garante que uma exclusão pelo slot
      // `paddingCheckbox` vença o `root` de forma confiável.
      root: {
        '&:not(.MuiTableCell-paddingCheckbox)': {
          padding: TABLE_SHAPE.cellPadding,
        },
      },
      // Só o peso: a cor de variant="head" já é text.primary por default do MUI, o
      // mesmo rgba(0,0,0,.87) do mixin legado.
      head: { fontWeight: TABLE_SHAPE.headerFontWeight },
    },
  },
  MuiTableBody: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiTableRow-root:nth-of-type(even)': {
          backgroundColor: alpha(theme.palette.common.black, TABLE_SHAPE.stripedRowAlpha),
        },
        '& .MuiTableRow-root:hover': {
          backgroundColor: alpha(theme.palette.common.black, TABLE_SHAPE.hoverRowAlpha),
        },
      }),
    },
  },
  MuiTablePagination: {
    defaultProps: {
      showFirstButton: true,
      showLastButton: true,
    },
  },
};

/** Ponto único de criação de tema: shell e páginas compartilham a mesma composição. */
export function createAppTheme(options: AppThemeOptions = {}): Theme {
  return createTheme({
    // Substituição integral das cinco chaves: impede o MUI de reintroduzir sm: 600.
    breakpoints: { values: { ...BREAKPOINTS } },
    palette: buildPalette(options),
    typography,
    shape: { borderRadius: BORDER_RADIUS },
    spacing: SPACING_UNIT,
    components,
  });
}
